import PromoCellRenderer from "../cell-renderers/PromoCellRenderer";
import ErrorCellRenderer from "../cell-renderers/ErrorCellRenderer";
import DateCellRenderer from "../../../../../components/AGGrid/cell-renderers/DateCellRenderer";
import TPRTypeCellRenderer from "../cell-renderers/TPRTypeCellRenderer";
import PPTStatusCellRenderer from "../cell-renderers/PPTStatusCellRenderer";
import DaysFromStartCellRenderer from "../cell-renderers/DaysFromStartCellRenderer";
import BadgeListCellRenderer from "../cell-renderers/BadgeListCellRenderer";
import {
  nameValueFormatter,
  stageComparator,
  nameComparator,
} from "ppt/utilities";

export const defaultColumnDef = {
  resizable: true,
  wrapText: true,
  autoHeight: true,
  width: 80,
  sortable: true,
  filter: "agSetColumnFilter",
  suppressHeaderMenuButton: false,
  menuTabs: ["filterMenuTab"],
  cellDataType: false,
  lockVisible: false,
  suppressColumnsToolPanel: false,
};

export const columnDefs = [
  {
    // Days from start date, calculated on the backend.
    // Negative = days before start (future, shows as number), positive = days after start ("Started +X").
    // Sortable numerically so the operator can see "starts soonest" up top:
    // most negative (starts soonest) → 0 (started today) → positive (already started).
    colId: "daysFromStart",
    headerName: "Days From Start",
    cellRenderer: DaysFromStartCellRenderer,
    valueGetter: (params) => params.data?.daysFromStart ?? null,
    filter: "agNumberColumnFilter",
    width: 130,
    maxWidth: 150,
  },
  {
    field: "currentStage",
    headerName: "Stage",
    cellRenderer: PPTStatusCellRenderer,
    valueFormatter: nameValueFormatter,
    filterValueGetter: (params) => {
      if (!params.data) return null;
      if (params.data.currentStage?.name) {
        return params.data.currentStage.name;
      }
      const stages = params.data.stages ?? [];
      const allComplete =
        stages.length > 0 && stages.every((s) => s?.state === "complete");
      return allComplete ? "Done" : "Stage unavailable";
    },
    comparator: stageComparator,
    width: 120,
  },
  {
    field: "name",
    headerName: "Promo",
    cellRenderer: PromoCellRenderer,
    flex: 3,
    minWidth: 150,
  },
  {
    field: "tprType",
    headerName: "TPR Type",
    cellRenderer: TPRTypeCellRenderer,
    valueFormatter: nameValueFormatter,
    filterValueGetter: (params) => params.data?.tprType?.name || "",
    comparator: nameComparator, // Need a comparator because we're not using a valueGetter and passing the object to the cellRenderer
    getQuickFilterText: () => "", // Exclude from quick filter
    width: 180,
  },
  {
    field: "territories",
    headerName: "Territories",
    valueFormatter: ({ value }) =>
      Array.isArray(value) ? value.join(", ") : value,
    cellRenderer: BadgeListCellRenderer,
    cellRendererParams: { maxItems: 2 },
    width: 200,
  },
  {
    field: "startDate",
    headerName: "Promo Start",
    cellRenderer: DateCellRenderer,
    cellRendererParams: { long: true },
    filter: "agDateColumnFilter",
    width: 135,
  },
  {
    field: "endDate",
    headerName: "Promo End",
    cellRenderer: DateCellRenderer,
    cellRendererParams: { long: true },
    filter: "agDateColumnFilter",
    width: 135,
  },
  {
    field: "clients",
    headerName: "Clients",
    // Backend returns clients as a flat List<String> of resolved display names
    // (apollo-id curated name → Rightsline short name fallback). Same shape on
    // both PromotionSummaryResponse and PromotionDetailResponse.
    valueGetter: (params) => params.data?.clients || [],
    filterValueGetter: (params) => params.data?.clients || [],
    cellRenderer: BadgeListCellRenderer,
    cellRendererParams: { maxItems: 1 },
    width: 150,
  },
  {
    field: "issueCount",
    headerName: "Issues",
    cellRenderer: ErrorCellRenderer,
    filter: "agNumberColumnFilter",
    width: 90,
    maxWidth: 100,
  },
  // Hidden fields not displayed in grid but available for filtering
  {
    field: "releaseId",
    headerName: "Release ID",
    hide: true,
    getQuickFilterText: (params) => params.value || "",
  },
  {
    field: "releaseOwner",
    headerName: "Created By",
    hide: true,
    width: 150,
  },
];
